import { ChevronDown, Info } from 'lucide-react';

import type { RoiDataset } from '../types/roi';
import { formatCurrency } from '../utils/format';

interface RoiAssumptionsPanelProps {
  dataset: RoiDataset;
}

export function RoiAssumptionsPanel({ dataset }: RoiAssumptionsPanelProps) {
  const { assumptions } = dataset;
  const recallPct = Math.round(
    (dataset.caughtProjects / dataset.totalProjects) * 100
  );
  return (
    <section className="w-full">
      <details className="group w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)]">
        <summary className="flex cursor-pointer items-center justify-between px-5 py-3 outline-none [&::-webkit-details-marker]:hidden">
          <div className="flex items-center gap-2.5">
            <Info className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
              How we calculated this
            </span>
          </div>
          <ChevronDown className="h-4 w-4 text-slate-400 transition-transform duration-300 group-open:rotate-180" />
        </summary>

        <div className="grid grid-cols-2 gap-x-6 gap-y-4 border-t border-gray-100 px-5 py-5 md:grid-cols-4">
          <AssumptionItem
            label="Time window"
            value={assumptions.timeWindowLabel}
          />
          <AssumptionItem
            label="Engineering headcount"
            value={`${assumptions.engineeringHeadcount.toLocaleString(
              'en-US'
            )} FTE`}
          />
          <AssumptionItem
            label="Blended fully-loaded cost"
            value={`${formatCurrency(assumptions.blendedSalaryUsd)} / yr`}
          />
          <AssumptionItem
            label="Per-day rate"
            value={`${formatCurrency(
              assumptions.ratePerEngineerDayUsd
            )} / engineer-day`}
          />
          <AssumptionItem
            label="Recall"
            value={`${dataset.caughtProjects} / ${dataset.totalProjects} (${recallPct}%)`}
          />
          <AssumptionItem
            label="Working days / year"
            value={`${assumptions.workingDaysPerYear} days`}
          />
        </div>

        <div className="space-y-3 border-t border-gray-100 bg-slate-50/30 px-5 py-5 text-[13px] leading-relaxed text-slate-600">
          <p>
            <span className="font-semibold text-slate-800">
              Only engineering rework is monetized.
            </span>{' '}
            Customer churn, lost revenue from missed launches, support
            escalations, bug-fix cost, and brand impact are deliberately
            excluded - they vary too much per company to put a defensible number
            on. The hero figure comes from a single top-down formula -
            recoverable rework hours per engineer per month, multiplied by
            headcount, blended hourly cost, and twelve months - not from summing
            per-project dollars. Late shipping and post-launch firefighting are
            reported as counts (projects, weeks, incidents, hotfix PRs) for the
            same reason.
          </p>
          <p>
            Of the {dataset.totalProjects} troubled projects in this dataset,
            Intera would have raised a signal in time on{' '}
            {dataset.caughtProjects}. The remaining {dataset.missedProjects}{' '}
            projects have failure signatures outside what Intera currently looks
            at (vendor delays, regulatory change, strategy pivots, key-person
            attrition). Those projects appear in the audit log above but are
            excluded from the late-ship and firefight counts.
          </p>
        </div>
      </details>
    </section>
  );
}

interface AssumptionItemProps {
  label: string;
  value: string;
}

function AssumptionItem({ label, value }: AssumptionItemProps) {
  return (
    <div>
      <span className="mb-1 block text-[10px] uppercase tracking-wider text-slate-400">
        {label}
      </span>
      <span className="block border-b border-gray-200 pb-0.5 text-sm font-semibold tabular-nums text-slate-900">
        {value}
      </span>
    </div>
  );
}
